import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLES, canAssignRole, formatInZone, type Role } from '@system-atlas/shared';
import { ApiError, api, type UserRow } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  Label,
  Modal,
  Select,
  Spinner,
  TextInput,
} from '../components/ui';
import { useToast } from '../components/toast';
import { UserGroupsAdmin } from '../components/UserGroupsAdmin';
import { DeleteUserDialog } from '../components/DeleteUserDialog';

export default function UsersAdminPage() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ message: string; password?: string } | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);

  const assignableRoles = ROLES.filter((r) => (me ? canAssignRole(me.role, r) : false));

  const load = async () => {
    try {
      const { users: rows } = await api.listUsers();
      setUsers(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const editUser = users?.find((u) => u.id === editUserId) ?? null;

  const statusBadge = (u: UserRow) =>
    u.isActive ? <Badge tone="green">{t('users.active')}</Badge> : <Badge tone="amber">{t('users.inactive')}</Badge>;

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="text-2xl font-bold tracking-tight">{t('users.title')}</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('users.subtitle')}</p>

      {error && (
        <div className="mt-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      {notice && (
        <div className="mt-4">
          <Alert kind="success">
            <div>{notice.message}</div>
            {notice.password && (
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-base font-semibold">{notice.password}</span>
                <CopyButton value={notice.password} />
              </div>
            )}
          </Alert>
        </div>
      )}

      <div className="mt-6">
        <CreateUserForm
          assignableRoles={assignableRoles}
          onCreated={(message, password) => {
            setNotice({ message, password });
            void load();
          }}
          onError={setError}
        />
      </div>

      <div className="mt-6">
        {!users ? (
          <Card className="p-0">
            <Spinner />
          </Card>
        ) : users.length === 0 ? (
          <Card>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('users.empty')}</p>
          </Card>
        ) : (
          <>
            {/* Desktop (md+): klassische Tabelle */}
            <Card className="hidden overflow-x-auto p-0 md:block">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('users.tableCaption')}</caption>
                <thead className="border-b border-slate-200 text-left text-slate-500 dark:border-slate-700">
                  <tr>
                    <th scope="col" className="px-4 py-3">{t('users.colName')}</th>
                    <th scope="col" className="px-4 py-3">{t('users.colEmail')}</th>
                    <th scope="col" className="px-4 py-3">{t('users.colRole')}</th>
                    <th scope="col" className="px-4 py-3">{t('users.colStatus')}</th>
                    <th scope="col" className="px-4 py-3">{t('users.colCreated')}</th>
                    <th scope="col" className="px-4 py-3 text-right">{t('users.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 last:border-0 dark:border-slate-700/60">
                      <td className="px-4 py-3 font-medium">{u.displayName}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{u.email}</td>
                      <td className="px-4 py-3">{t(`roles.${u.role}`)}</td>
                      <td className="px-4 py-3">{statusBadge(u)}</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{formatInZone(u.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <Button variant="secondary" onClick={() => setEditUserId(u.id)}>
                            {t('users.edit')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Mobil (< md): eine Karte pro Benutzer mit allen Feldern */}
            <ul className="space-y-3 md:hidden">
              {users.map((u) => (
                <li key={u.id}>
                  <Card className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{u.displayName}</p>
                        <p className="truncate text-sm text-slate-600 dark:text-slate-300">{u.email}</p>
                      </div>
                      {statusBadge(u)}
                    </div>
                    <dl className="grid grid-cols-2 gap-y-1 text-sm">
                      <dt className="text-slate-500 dark:text-slate-400">{t('users.colRole')}</dt>
                      <dd className="text-right">{t(`roles.${u.role}`)}</dd>
                      <dt className="text-slate-500 dark:text-slate-400">{t('users.colCreated')}</dt>
                      <dd className="text-right text-slate-500 dark:text-slate-400">{formatInZone(u.createdAt)}</dd>
                    </dl>
                    <div className="flex justify-end">
                      <Button variant="secondary" onClick={() => setEditUserId(u.id)}>
                        {t('users.edit')}
                      </Button>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <UserEditModal
        user={editUser}
        meId={me?.id ?? ''}
        assignableRoles={assignableRoles}
        onClose={() => setEditUserId(null)}
        onChanged={load}
      />

      <UserGroupsAdmin />
    </div>
  );
}

function UserEditModal({
  user,
  meId,
  assignableRoles,
  onClose,
  onChanged,
}: {
  user: UserRow | null;
  meId: string;
  assignableRoles: Role[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [mfaResetOpen, setMfaResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isSelf = user?.id === meId;

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
      setEmail(user.email);
      setTempPassword(null);
    }
  }, [user?.id]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    await act(async () => {
      await api.updateUser(user.id, { displayName: displayName.trim(), email: email.trim() });
      toast.success(t('users.updated'));
    });
  };

  if (!user) return null;

  return (
    <Modal open title={t('users.editTitle')} onClose={onClose}>
      <form onSubmit={saveProfile} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="eu-name">{t('users.colName')}</Label>
          <TextInput id="eu-name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="eu-email">{t('users.colEmail')}</Label>
          <TextInput id="eu-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('users.saveProfile')}
          </Button>
        </div>
      </form>

      <div className="mt-5 space-y-4 border-t border-slate-200 pt-4 dark:border-slate-700">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="eu-role">{t('users.colRole')}</Label>
          <Select
            id="eu-role"
            value={user.role}
            disabled={busy || isSelf || !assignableRoles.includes(user.role)}
            onChange={(e) => void act(() => api.setUserRole(user.id, e.target.value as Role))}
          >
            {ROLES.map((r) => (
              <option key={r} value={r} disabled={!assignableRoles.includes(r)}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('users.colStatus')}</span>
          <div className="flex items-center gap-2">
            {user.isActive ? (
              <Badge tone="green">{t('users.active')}</Badge>
            ) : (
              <Badge tone="amber">{t('users.inactive')}</Badge>
            )}
            <Button
              variant="secondary"
              disabled={busy || isSelf}
              onClick={() => void act(() => api.setUserStatus(user.id, !user.isActive))}
            >
              {user.isActive ? t('users.deactivate') : t('users.activate')}
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('auth.password')}</span>
          <Button
            variant="secondary"
            disabled={busy || isSelf}
            onClick={() =>
              void act(async () => {
                const { tempPassword: pw } = await api.resetUserPassword(user.id);
                setTempPassword(pw);
              })
            }
          >
            {t('users.resetPassword')}
          </Button>
        </div>
        {tempPassword && (
          <Alert kind="success">
            <div>{t('users.resetDone', { name: user.displayName })}</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-base font-semibold">{tempPassword}</span>
              <CopyButton value={tempPassword} />
            </div>
          </Alert>
        )}
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{t('users.mfa')}</span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {user.totpEnabled ? (
              <Badge tone="green">{t('users.mfaActive')}</Badge>
            ) : (
              <Badge tone="amber">{t('users.mfaInactive')}</Badge>
            )}
            {user.mfaRequired && <Badge tone="neutral">{t('users.mfaEnforced')}</Badge>}
            <Button
              variant="secondary"
              disabled={busy || isSelf}
              onClick={() => void act(() => api.setUserMfaRequired(user.id, !user.mfaRequired))}
            >
              {user.mfaRequired ? t('users.mfaUnrequire') : t('users.mfaRequire')}
            </Button>
            <Button
              variant="secondary"
              disabled={busy || isSelf || !user.totpEnabled}
              onClick={() => setMfaResetOpen(true)}
            >
              {t('users.mfaReset')}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-5 border-t border-red-200 pt-4 dark:border-red-500/30">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">{t('users.deleteAction')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('users.deleteHint')}</p>
          </div>
          <Button variant="danger" disabled={busy || isSelf} onClick={() => setDeleteOpen(true)}>
            {t('users.deleteAction')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={mfaResetOpen}
        title={t('users.mfaReset')}
        message={t('users.mfaResetConfirm', { name: user.displayName })}
        confirmLabel={t('users.mfaReset')}
        onCancel={() => setMfaResetOpen(false)}
        onConfirm={() => {
          setMfaResetOpen(false);
          void act(async () => {
            await api.resetUserMfa(user.id);
            toast.success(t('users.mfaResetDone'));
          });
        }}
      />

      <DeleteUserDialog
        user={deleteOpen ? user : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(deleted) => {
          setDeleteOpen(false);
          onClose();
          toast.success(t('users.deleteDone', { name: deleted.displayName }));
          void onChanged();
        }}
      />

      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          {t('users.close')}
        </Button>
      </div>
    </Modal>
  );
}

function CreateUserForm({
  assignableRoles,
  onCreated,
  onError,
}: {
  assignableRoles: Role[];
  onCreated: (message: string, password?: string) => void;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { tempPassword } = await api.createUser({ email, displayName, role });
      onCreated(t('users.createdDone', { email }), tempPassword);
      setEmail('');
      setDisplayName('');
      setRole('user');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-lg font-semibold">{t('users.createTitle')}</h2>
      <form onSubmit={submit} className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Label htmlFor="cu-name">{t('users.colName')}</Label>
          <TextInput id="cu-name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cu-email">{t('users.colEmail')}</Label>
          <TextInput id="cu-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="cu-role">{t('users.colRole')}</Label>
          <Select id="cu-role" className="w-full" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-end">
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('users.create')}
          </Button>
        </div>
      </form>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('users.createHint')}</p>
    </Card>
  );
}
